/**
 * Tournament add-on eligibility for the seated player (EN-11059).
 *
 * Two independent questions decide whether the Add-On button is offered:
 *
 * 1. Is the add-on WINDOW open? The gotTexas `game.canAddOnChips` flag is
 *    table-wide (break + level + config, no per-player term) and arrives with
 *    every table update, so it is the reliable "window open" signal. The
 *    player's gotTnmStatus record carries the same window check, OR'd in for
 *    tables where gotTexas hasn't landed yet. Neither is trusted to say the
 *    window is CLOSED on its own — a status record pushed before the break
 *    still reads false when the break starts (the CX-1836 "no button" symptom).
 *
 * 2. Does THIS player still have an add-on left? Counted from the larger of the
 *    status record's `myAddons` and the count the engine confirmed in its last
 *    gotTnmAddon reply. Both counts can lag (gotTnmStatus is not re-pushed on a
 *    purchase) but never over-count, so taking the max can only hide the
 *    button once the engine itself would refuse the add-on.
 */

export type PlayerTournamentStatus = {
  tournamentId?: string | number;
  playerId?: string;
  canAddOnChips?: boolean;
  addOn?: number | string;
  myAddons?: number | string;
};

/** The engine's add-on count for one tournament, from its gotTnmAddon reply. */
export type ConfirmedAddOns = {
  tournamentId: string;
  count: number;
};

export const findPlayerTournamentStatus = (
  tournamentStatus: PlayerTournamentStatus[] | undefined,
  tournamentId: string | number | undefined,
  playerGUID: string | undefined
): PlayerTournamentStatus | undefined =>
  tournamentStatus?.find((s) => s.tournamentId === tournamentId && s.playerId === playerGUID);

const toCount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const confirmedCountFor = (
  confirmed: ConfirmedAddOns | null | undefined,
  tournamentId: string | number | undefined
): number =>
  confirmed && tournamentId !== undefined && confirmed.tournamentId === String(tournamentId)
    ? toCount(confirmed.count)
    : 0;

type AddOnCountInput = {
  playerStatus: PlayerTournamentStatus | undefined;
  /** gotTexas `game.addOn` — the tournament's add-on limit, if carried. */
  tableAddOnLimit: unknown;
  confirmedAddOns: ConfirmedAddOns | null | undefined;
  tournamentId: string | number | undefined;
};

/**
 * Add-ons this player can still buy, or `null` when the limit is unknown
 * (no status record and no table limit yet). Callers must treat `null` as
 * "don't know", not "none left".
 */
export const remainingAddOns = ({
  playerStatus,
  tableAddOnLimit,
  confirmedAddOns,
  tournamentId
}: AddOnCountInput): number | null => {
  const limit = toCount(playerStatus?.addOn ?? tableAddOnLimit);
  if (limit === 0) return null;
  const used = Math.max(
    toCount(playerStatus?.myAddons),
    confirmedCountFor(confirmedAddOns, tournamentId)
  );
  return Math.max(0, limit - used);
};

type AddOnOfferInput = AddOnCountInput & {
  /** gotTexas `game.canAddOnChips` — table-wide window flag. */
  tableWindowOpen: unknown;
};

export const isAddOnOffered = (input: AddOnOfferInput): boolean => {
  const windowOpen = Boolean(input.tableWindowOpen) || Boolean(input.playerStatus?.canAddOnChips);
  if (!windowOpen) return false;
  return remainingAddOns(input) !== 0;
};
